import { User } from "../types/Types";
import {
  EXPLORER_SEGMENT_EARLY_STAGE,
  EXPLORER_SEGMENT_ESTABLISHED,
  EXPLORER_SEGMENT_SME,
  normalizeExplorerSegment,
} from "../explorer/explorerSegments";
import {
  isPaidPortalMember,
  isYouthOrEarlyStageMembership,
} from "./membershipTier";
import {
  getMembershipBadgeI18nKey,
  getMembershipListLabel,
  getMembershipProfileBadgeTheme,
  normalizeMembershipTier,
  resolveMembershipIdentity,
  type MembershipIdentitySource,
} from "./membershipIdentity";
import { resolveProfessionalPhotoUrl } from "./profilePhoto";

/**
 * Azure AD / B2C object id from a user DTO. APIs may send `objectId` or `ObjectId`.
 */
export function resolvedObjectId(
  u: Pick<User, "objectId" | "id"> | null | undefined
): string {
  if (!u) return "";
  const r = u as User & { ObjectId?: string };
  return String(r.objectId ?? r.ObjectId ?? "").trim();
}

export function getAvatarUrl(user: User): string {
  return resolveProfessionalPhotoUrl(user);
}

export function getInitials(user: User): string {
  const first = (user.firstName || "").trim().charAt(0).toUpperCase();
  const last = (user.lastName || "").trim().charAt(0).toUpperCase();
  return first && last ? `${first}${last}` : first || last || "?";
}

export function getRole(user: User): string {
  // Youth/Early Stage study interest only applies to paid members — not unpaid Explorers
  // who may have a draft membershipName from the registration wizard.
  if (
    isPaidPortalMember(user) &&
    isYouthOrEarlyStageMembership(user.membershipName)
  ) {
    return user.studyInterest || "";
  }
  return user.personalPositionCompany || user.businessSectorIndustry || "";
}

export function getMembershipLabel(name: string): string {
  return getMembershipListLabel({ membershipName: name }) || name;
}

/** i18n key for connections list membership badge (`connections.memberBadge.*`). */
export function getMembershipBadgeKey(
  target: string | ConnectionTarget,
): string {
  return getMembershipBadgeI18nKey(target);
}

export type UpgradePromptKind = "executive" | "business";

/** Explorer step-2 segment → tier used only for connection permission rules (not display). */
const EXPLORER_SEGMENT_MEMBERSHIP_TIER: Record<string, string> = {
  [EXPLORER_SEGMENT_EARLY_STAGE]: "Youth Membership",
  [EXPLORER_SEGMENT_SME]: "Business Membership",
  [EXPLORER_SEGMENT_ESTABLISHED]: "Executive Membership",
};

export type ConnectionTarget = MembershipIdentitySource;

export type ExplorerMeForConnection = {
  explorer?: unknown | null;
  payload?: { segment?: string } | null;
};

/** Display / badge Explorer check — delegates to resolveMembershipIdentity. */
export function isExplorerTarget(
  user: ConnectionTarget | null | undefined,
): boolean {
  return resolveMembershipIdentity(user).isExplorer;
}

/** Explorer only: paid membershipName and/or step-2 segmentation tier for connection rules. */
export function buildConnectionTargetFromUser(
  user: ConnectionTarget | null | undefined,
  explorerMe?: ExplorerMeForConnection | null,
  isExplorer = false
): ConnectionTarget {
  const r = user as User & { MembershipName?: string | null };
  const membershipName = String(user?.membershipName ?? r?.MembershipName ?? "").trim();

  if (!isExplorer) {
    return {
      membershipName,
      explorerAttendeeType: "",
      hasPaid: user?.hasPaid,
      stripeStatus: user?.stripeStatus,
      userCategory: user?.userCategory,
    };
  }

  const fromUser = resolveExplorerAttendeeType(user);
  const explorerRow = explorerMe?.explorer as
    | { attendeeType?: string; AttendeeType?: string }
    | null
    | undefined;
  const fromExplorer =
    String(explorerRow?.attendeeType ?? explorerRow?.AttendeeType ?? "").trim() ||
    String(explorerMe?.payload?.segment ?? "").trim();
  return {
    // Draft membershipName during unpaid registration must not count as paid tier.
    membershipName: "",
    explorerAttendeeType: fromUser || fromExplorer || "",
    hasPaid: user?.hasPaid ?? 0,
    stripeStatus: user?.stripeStatus,
    userCategory: "Explorer",
  };
}

/**
 * Current user's tier for getConnectionPermission: paid members use membershipName only;
 * Explorers use DDL segmentation (step 2) via buildConnectionTargetFromUser.
 */
export function resolveCurrentUserConnectionSource(
  user: ConnectionTarget | null | undefined,
  explorerMe: ExplorerMeForConnection | null | undefined,
  isExplorer: boolean
): ConnectionTarget | string | undefined {
  if (!user) return undefined;
  if (!isExplorer) return user.membershipName ?? undefined;
  return buildConnectionTargetFromUser(user, explorerMe, true);
}

export function resolveExplorerAttendeeType(
  user:
    | Pick<User, "explorerAttendeeType" | "userCategory">
    | MembershipIdentitySource
    | null
    | undefined,
): string {
  if (!user) return "";
  const r = user as User & {
    ExplorerAttendeeType?: string | null;
    explorerAttendeeType?: string | null;
  };
  return String(r.explorerAttendeeType ?? r.ExplorerAttendeeType ?? "").trim();
}

function resolveMembershipName(
  user: ConnectionTarget | null | undefined
): string {
  if (!user) return "";
  const r = user as User & { MembershipName?: string | null };
  return String(user?.membershipName ?? r?.MembershipName ?? "").trim();
}

/**
 * Connections list badge — single identity util (Explorer vs paid tier).
 */
export function getConnectionMembershipDisplay(
  user: ConnectionTarget | null | undefined,
): string {
  return getMembershipListLabel(user);
}

/** Profile curved badge — single identity util. */
export function getProfileMembershipBadgeTheme(
  user: ConnectionTarget | null | undefined,
): { stroke: string; text: string } | null {
  return getMembershipProfileBadgeTheme(user);
}

/**
 * Tier for connection rules / badges: active paid membership first;
 * unpaid Explorers use segmentation only (draft membershipName ignored).
 */
export function resolveConnectionTargetMembership(
  user: ConnectionTarget | null | undefined
): string {
  if (!isExplorerTarget(user)) {
    const membershipName = resolveMembershipName(user);
    if (normalizeMembership(membershipName)) {
      return membershipName;
    }
  }

  const segment = normalizeExplorerSegment(resolveExplorerAttendeeType(user));
  const fromSegment = segment ? EXPLORER_SEGMENT_MEMBERSHIP_TIER[segment] : "";
  if (fromSegment) return fromSegment;

  return "";
}

export function normalizeMembership(
  m: string | undefined,
): "Youth Membership" | "Early Stage Membership" | "Business Membership" | "Executive Membership" | "" {
  return normalizeMembershipTier(m);
}

/** Youth and Early Stage are one connection tier (legacy Youth + new Early Stage). */
function isYouthConnectionTier(
  tier: ReturnType<typeof normalizeMembership>,
): boolean {
  return isYouthOrEarlyStageMembership(tier);
}

/**
 * Connection permission: who can send a connection request to whom.
 * Youth / Early Stage → same tier; Business → Youth + Early Stage + Business; Executive → all.
 * Explorer senders skip tier checks and may request any membership (weekly limits still apply).
 * Explorer targets use paid membership when present, else segmentation tier (step 2).
 */
export function getConnectionPermission(
  current: ConnectionTarget | string | undefined,
  target: ConnectionTarget | string,
  isExplorerCurrentUser = false
): {
  canConnect: boolean;
  upgradeMessage: string;
  upgradeKind: UpgradePromptKind | "none";
} {
  if (isExplorerCurrentUser) {
    return { canConnect: true, upgradeMessage: "", upgradeKind: "none" };
  }

  const targetMembership =
    typeof target === "string"
      ? target
      : resolveConnectionTargetMembership(target);
  const currentMembership =
    typeof current === "string"
      ? current
      : resolveConnectionTargetMembership(current);
  const currentTier = normalizeMembership(currentMembership);
  const targetTier = normalizeMembership(targetMembership);
  let canConnect = false;
  let upgradeMessage = "";
  let upgradeKind: UpgradePromptKind | "none" = "none";

  if (!currentTier || !targetTier) {
    return { canConnect: false, upgradeMessage: "", upgradeKind: "none" };
  }

  if (currentTier === targetTier) canConnect = true;
  else if (isYouthConnectionTier(currentTier) && isYouthConnectionTier(targetTier))
    canConnect = true;
  else if (currentTier === "Executive Membership") canConnect = true;
  else if (currentTier === "Business Membership") {
    if (targetTier === "Youth Membership" || targetTier === "Early Stage Membership")
      canConnect = true;
    else {
      upgradeMessage = "Upgrade to Executive membership";
      upgradeKind = "executive";
    }
  } else if (currentTier === "Youth Membership" || currentTier === "Early Stage Membership") {
    if (targetTier === "Business Membership") {
      upgradeMessage = "Upgrade to Business membership";
      upgradeKind = "business";
    } else if (targetTier === "Executive Membership") {
      upgradeMessage = "Upgrade to Executive membership";
      upgradeKind = "executive";
    }
  }

  return { canConnect, upgradeMessage, upgradeKind };
}